/* eslint-disable @typescript-eslint/no-explicit-any  */
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import axios from 'axios'
import toast from 'react-hot-toast'

import CookieManager from '@/core/hooks/useCookie'
import { PlansLimitErrorEventEmitter } from '@/core/utils/plansLimitErrorEventEmitter'
import { t } from '@/core/i18n/setting'

export type MethodType = 'get' | 'post' | 'put' | 'delete'

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipGlobalErrorHandler?: boolean
}

interface ErrorResponseData {
  error?: {
    message?: string
    displayMode?: string
  }
}

type ApiErrorResponse = ErrorResponseData | ErrorResponseData[] | string

/**
 * HttpAdapter interface defines the shape for any HTTP library adapter.
 * It allows swapping out implementations (e.g., fetch, axios) with minimal changes.
 */
export interface HttpAdapter {
  instance: AxiosInstance
  request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R>
}

/**
 * AxiosAdapter wraps an AxiosInstance to conform to HttpAdapter.
 */
export class AxiosAdapter implements HttpAdapter {
  public instance: AxiosInstance

  constructor(config: AxiosRequestConfig) {
    this.instance = axios.create(config)
    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor for logging or headers
    this.instance.interceptors.request.use(
      config => {
        console.debug('[HTTP Request]', config.method?.toUpperCase(), config.url, config.params || config.data)

        // Read auth_token cookie and set authorization header
        const authToken = CookieManager.getCookie('auth_token')

        if (authToken) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${authToken}`
        }

        // Detect and set Content-Type automatically
        if (config.data instanceof FormData) {
          delete config.headers?.['Content-Type']
        }

        return config
      },
      (error: AxiosError) => {
        console.error('[HTTP Request Error]', error)

        return Promise.reject(error)
      }
    )

    // Response interceptor for logging errors
    this.instance.interceptors.response.use(
      response => response.data,
      (error: AxiosError) => {
        console.error('[HTTP Response Error]', error.response?.status, error.response?.data || error.message)

        const skipGlobalErrorHandler = (error.config as CustomAxiosRequestConfig)?.skipGlobalErrorHandler

        if (!skipGlobalErrorHandler && error.response) {
          const status = error.response.status
          const responseData = error.response.data as ApiErrorResponse

          const errorData =
            typeof responseData === 'object' && !Array.isArray(responseData) ? responseData.error : undefined

          const errorMessage = errorData?.message || t('somethingWentWrong')

          if (status === 400 && errorData?.displayMode?.toLowerCase() === 'popup') {
            PlansLimitErrorEventEmitter.emit({
              message: errorMessage
            })
          } else {
            let toastMessage = errorMessage

            if (status === 401) {
              toastMessage = t('unauthorized401')
            } else if (status === 404) {
              toastMessage = t('pageNotFoundMessage')
            } else if (status >= 500) {
              toastMessage = t('somethingWentWrong')
            } else if (typeof responseData === 'string') {
              toastMessage = responseData
            }

            toast.error(toastMessage, {
              id: 'api-error'
            })
          }
        } else if (!skipGlobalErrorHandler && error.request) {
          toast.error(t('networkError'), {
            id: 'networkError'
          })
        } else if (!skipGlobalErrorHandler) {
          toast.error(t('somethingWentWrong'), {
            id: 'somethingWentWrong'
          })
        }

        return Promise.reject(error)
      }
    )
  }

  public request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
    return this.instance.request<T, R>(config)
  }
}

/**
 * HttpClient is a wrapper that uses an HttpAdapter for requests,
 * providing a consistent interface for API communication.
 */
export class HttpClient {
  private adapter: HttpAdapter

  constructor(adapter: HttpAdapter) {
    this.adapter = adapter
  }

  /** Generic GET request */
  public get<T = any>(url: string, config?: CustomAxiosRequestConfig) {
    return this.adapter.request<T>({ method: 'GET', url, ...config })
  }

  /** Generic POST request */
  public post<T = any, D = any>(url: string, data: D, config?: CustomAxiosRequestConfig) {
    return this.adapter.request<T>({ method: 'POST', url, data, ...config })
  }

  /** Generic PUT request */
  public put<T = any, D = any>(url: string, data: D, config?: CustomAxiosRequestConfig) {
    return this.adapter.request<T>({ method: 'PUT', url, data, ...config })
  }

  /** Generic DELETE request */
  public delete<T = any>(url: string, config?: CustomAxiosRequestConfig) {
    return this.adapter.request<T>({ method: 'DELETE', url, ...config })
  }
}

/**
 * Example: Create a singleton HttpClient with AxiosAdapter
 */
const apiConfig: AxiosRequestConfig = {
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  timeout: 10000

  // headers: {
  //   'Content-Type': 'application/json'
  // }
}

export const httpClient = new HttpClient(new AxiosAdapter(apiConfig))
